// Cart: item codes + qty in localStorage; prices always come from the server.
import { $, $$, api, esc, icon, money, partArt, showPrices, store, t, toast } from "./util";
import { initDeliveryAddress, rememberAddress } from "./address";

const KEY = "darat_cart";
const PAYING = "darat_paying";      // {id, token, t}: an online order placed from this cart, not paid yet
const DRAFT = "darat_ck_draft";     // checkout details typed on this device (kept across reloads)
let lines = store(KEY) || [];
let priced = null;
let pricing = null;
let goodsTotal = 0;  // cart total incl. VAT after any coupon; the delivery fee is added on top

// Delivery fee shown at checkout. Display only: the server recomputes it (storefront/delivery.py).
function deliveryConfig(form) {
	try { return JSON.parse(form?.dataset.delivery || "{}"); } catch (e) { return {}; }
}
export function applyDelivery() {
	const form = document.querySelector("[data-checkout-form]");
	const cfg = deliveryConfig(form);
	const delivery = cfg.enabled && form?.querySelector("[name=fulfilment]:checked")?.value === "Delivery";
	let fee = 0, cod = 0;
	if (delivery) {
		const pay = form.querySelector("[name=payment]:checked")?.value || "cod";
		fee = cfg.free_over && goodsTotal >= cfg.free_over ? 0 : (cfg.fee || 0);
		cod = pay === "cod" ? (cfg.cod_fee || 0) : 0;  // the COD fee stays even when delivery is free
	}
	const row = document.querySelector("[data-ship-row]");
	if (row) {
		row.hidden = !delivery;
		const v = row.querySelector("[data-ship-val]");
		if (v) v.textContent = fee ? money(fee) : t("مجاناً", "Free");
	}
	const codRow = document.querySelector("[data-cod-row]");
	if (codRow) {
		codRow.hidden = !cod;
		const v = codRow.querySelector("[data-cod-val]");
		if (v) v.textContent = money(cod);
	}
	$$("[data-cart-total]").forEach((e) => (e.textContent = money(goodsTotal + fee + cod)));
	const bar = document.querySelector("[data-free-bar]");
	if (bar) {  // "add X more for free delivery"
		const show = delivery && cfg.free_over > 0 && cfg.fee > 0;
		bar.hidden = !show;
		if (show) {
			const left = Math.max(0, cfg.free_over - goodsTotal);
			bar.querySelector("[data-free-text]").textContent = left > 0
				? t(`أضف ${money(left)} للحصول على توصيل مجاني`, `Add ${money(left)} more for free delivery`)
				: t("طلبك مؤهل للتوصيل المجاني", "Your order qualifies for free delivery");
			bar.querySelector("[data-free-fill]").style.width = `${Math.min(100, (goodsTotal / cfg.free_over) * 100)}%`;
		}
	}
}

export const cart = {
	lines: () => lines,
	count: () => lines.reduce((s, l) => s + l.qty, 0),
	save() {
		lines = lines.filter((l) => l.qty > 0);
		store(KEY, lines);
		priced = null;
		renderCount(true);
		syncAddButtons();
		document.dispatchEvent(new CustomEvent("darat:cart"));
	},
	add(c, qty = 1) {
		const l = lines.find((x) => x.c === c);
		if (l) l.qty += qty; else lines.push({ c, qty });
		this.save();
	},
	set(c, qty) {
		const l = lines.find((x) => x.c === c);
		if (l) l.qty = Math.max(0, qty);
		this.save();
	},
	clear() { lines = []; this.save(); },
	async price() {
		if (priced) return priced;
		if (!lines.length) return (priced = { lines: [], total: 0, subtotal: 0, vat: 0, count: 0, problems: [] });
		pricing = pricing || api("darat.storefront.api.cart", { lines }, "POST").finally(() => { pricing = null; });
		priced = await pricing;
		// sync quantities the server reduced / removed
		const ok = new Map(priced.lines.map((l) => [l.c, l.qty]));
		let changed = false;
		lines.forEach((l) => { if (!ok.has(l.c) || ok.get(l.c) !== l.qty) { l.qty = ok.get(l.c) || 0; changed = true; } });
		if (changed) { lines = lines.filter((l) => l.qty > 0); store(KEY, lines); renderCount(); }
		return priced;
	},
};

// cards: an item already in the cart shows a − qty + stepper instead of the add button
export function syncAddButtons() {
	const q = new Map(lines.map((l) => [l.c, l.qty]));
	$$("[data-addwrap]").forEach((w) => {
		const c = w.dataset.addwrap, n = q.get(c) || 0;
		const has = w.querySelector(".ds-step");
		if (n && !has) {
			w.innerHTML = `<div class="ds-step"><button type="button" data-step="-1" aria-label="-">${n === 1 ? icon("trash") : icon("minus")}</button><b>${n}</b><button type="button" data-step="1" aria-label="+">${icon("plus")}</button></div>`;
		} else if (n && has) {
			has.querySelector("b").textContent = n;
			has.querySelector("[data-step='-1']").innerHTML = n === 1 ? icon("trash") : icon("minus");
		} else if (!n && has) {
			w.innerHTML = `<button class="ds-btn ds-btn--icon ds-btn--primary" type="button" data-add="${esc(c)}" aria-label="${t("أضف إلى السلة", "Add to cart")}">${icon("cart-plus")}</button>`;
		}
	});
}

function renderCount(bump) {
	const n = cart.count();
	$$("[data-cart-total-label]").forEach((el) => (el.textContent = n ? t(n === 1 ? "قطعة واحدة" : n === 2 ? "قطعتان" : `${n} قطع`, `${n} item${n > 1 ? "s" : ""}`) : t("فارغة", "Empty")));
	$$("[data-cart-count]").forEach((el) => {
		el.textContent = n;
		el.hidden = !n;
		if (bump && n) { el.classList.remove("is-bump"); void el.offsetWidth; el.classList.add("is-bump"); }
	});
}

function lineHTML(l, problems) {
	const p = problems.find((x) => x.c === l.c);
	return `<div class="ds-line" data-line="${esc(l.c)}">
		<a class="ds-line__img" href="${l.u ? "/p/" + esc(l.u) : "#"}" aria-label="${esc(l.n)}">${l.i ? `<img src="${esc(l.i)}" alt="">` : partArt(l.ic)}</a>
		<div class="ds-line__body">
			<b>${esc((document.documentElement.lang || "").startsWith("ar") && l.a ? l.a : l.n)}</b>
			<small>${esc(l.c)}${showPrices ? " · " + money(l.p) : ""}</small>
			${p && p.reason === "qty_reduced" ? `<span class="ds-line__warn">${t(`الكمية المتوفرة ${p.qty} فقط`, `Only ${p.qty} available`)}</span>` : ""}
			<div class="ds-line__ctrl">
				<div class="ds-qty ds-qty--sm"><button type="button" data-line-qty="-1" aria-label="-">${icon("minus")}</button><input type="number" value="${l.qty}" min="1" max="${l.stock}" aria-label="${t("الكمية", "Quantity")}"><button type="button" data-line-qty="1" aria-label="+">${icon("plus")}</button></div>
				<button type="button" class="ds-line__rm" data-line-rm aria-label="${t("حذف", "Remove")}">${icon("trash")}</button>
			</div>
		</div>
		${showPrices ? `<span class="ds-line__price">${money(l.line)}</span>` : ""}
	</div>`;
}

export async function renderCart() {
	document.documentElement.classList.remove("ds-cart-empty");  // JS takes over from the pre-paint guess
	const holders = $$("[data-cart-lines]");
	if (!holders.length) return;
	if (!lines.length) {
		holders.forEach((h) => (h.innerHTML = h.closest("[data-checkout]") ? "" : `<div class="ds-empty"><svg viewBox="0 0 160 120" class="ds-empty__art"><use href="#part-generic"/></svg><p>${t("سلتك فارغة", "Your cart is empty")}</p><a class="ds-btn ds-btn--primary" href="/shop">${t("تسوق الآن", "Start shopping")}</a></div>`));
		$$("[data-cart-summary]").forEach((e) => (e.hidden = true));
		$$("[data-cart-empty]").forEach((e) => (e.hidden = false));
		$$("[data-checkout], [data-ckbar], [data-suggest]").forEach((f) => (f.hidden = true));
		$$(".ds-steps li").forEach((li, i) => li.classList.toggle("is-on", i === 0));
		return;
	}
	holders.forEach((h) => { if (!h.children.length) h.innerHTML = `<div class="ds-skel" style="min-height:90px"></div>`; });
	let data;
	try { data = await cart.price(); } catch (e) { holders.forEach((h) => (h.innerHTML = `<p class="ds-formerror">${esc(e.message)}</p>`)); return; }
	const gone = data.problems.filter((p) => p.reason !== "qty_reduced");
	holders.forEach((h) => {
		h.innerHTML = data.lines.map((l) => lineHTML(l, data.problems)).join("") +
			(gone.length ? `<p class="ds-line__warn">${t("أزلنا من سلتك منتجات لم تعد متوفرة", "Some items are no longer available and were removed")}</p>` : "");
	});
	goodsTotal = data.total;
	$$("[data-cart-total]").forEach((e) => (e.textContent = money(data.total)));
	applyDelivery();
	$$("[data-cart-subtotal]").forEach((e) => (e.textContent = money(data.subtotal)));
	$$("[data-cart-vat]").forEach((e) => (e.textContent = money(data.vat)));
	$$("[data-cart-summary]").forEach((e) => (e.hidden = !data.lines.length));
	$$("[data-cart-empty]").forEach((e) => (e.hidden = !!data.lines.length));
	$$("[data-checkout], [data-ckbar]").forEach((f) => (f.hidden = !data.lines.length));
}

function flyToCart(from) {
	const target = document.querySelector(".ds-cartbtn:not([hidden])") || document.querySelector(".ds-tabbar [data-open=cart]");
	const img = from.closest(".ds-card, .ds-pdp")?.querySelector("img, .ds-art svg");
	if (!target || !img || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
	const a = img.getBoundingClientRect(), b = target.getBoundingClientRect();
	if (!b.width) return;
	const ghost = img.cloneNode(true);
	Object.assign(ghost.style, { position: "fixed", left: a.left + "px", top: a.top + "px", width: a.width + "px", height: a.height + "px", zIndex: 200, pointerEvents: "none", borderRadius: "16px", transition: "transform .8s cubic-bezier(.5,-0.2,.3,1), opacity .8s", objectFit: "contain" });
	document.body.appendChild(ghost);
	requestAnimationFrame(() => {
		const dx = b.left + b.width / 2 - (a.left + a.width / 2), dy = b.top + b.height / 2 - (a.top + a.height / 2);
		ghost.style.transform = `translate(${dx}px, ${dy}px) scale(.08)`;
		ghost.style.opacity = ".3";
	});
	setTimeout(() => ghost.remove(), 850);
}

export function initCart() {
	// the online order placed from this cart is now paid (its order page says so): empty the cart
	const paying = store(PAYING);
	if (paying && $("[data-order-paid]") && location.pathname.includes(encodeURIComponent(paying.token))) {
		store(PAYING, null); store(DRAFT, null); cart.clear();
	}
	renderCount();
	syncAddButtons();
	document.addEventListener("click", (e) => {
		const step = e.target.closest("[data-addwrap] [data-step]");
		if (step) {
			const c = step.closest("[data-addwrap]").dataset.addwrap;
			const cur = lines.find((l) => l.c === c);
			cart.set(c, (cur ? cur.qty : 0) + parseInt(step.dataset.step, 10));
			return;
		}
		const add = e.target.closest("[data-add]");
		if (add && !add.disabled) {
			e.preventDefault();
			let qty = 1;
			if (add.hasAttribute("data-with-qty")) qty = parseInt(add.closest("[data-buy]")?.querySelector("input")?.value || "1", 10) || 1;
			cart.add(add.dataset.add, qty);
			add.classList.remove("is-added"); void add.offsetWidth; add.classList.add("is-added");
			flyToCart(add);
			toast(`${icon("check-circle")}<span>${t("تمت الإضافة إلى السلة", "Added to cart")}</span><a href="/cart">${t("إتمام الطلب", "Checkout")}</a>`);
			return;
		}
		const now = e.target.closest("[data-buy-now]");
		if (now && !now.disabled) {
			const qty = parseInt(now.closest(".ds-pdp__info")?.querySelector("[data-buy] input")?.value || "1", 10) || 1;
			cart.add(now.dataset.buyNow, qty);
			location.href = "/cart";
			return;
		}
		const line = e.target.closest("[data-line]");
		if (line) {
			const c = line.dataset.line;
			const cur = lines.find((l) => l.c === c);
			if (e.target.closest("[data-line-rm]")) { line.style.opacity = ".3"; cart.set(c, 0); renderCart(); }
			const step = e.target.closest("[data-line-qty]");
			if (step && cur) { cart.set(c, cur.qty + parseInt(step.dataset.lineQty, 10)); renderCart(); }
		}
		const qbtn = e.target.closest("[data-qty]");
		if (qbtn) {
			const input = qbtn.parentElement.querySelector("input");
			const max = parseInt(input.max || "99", 10);
			input.value = Math.min(max, Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(qbtn.dataset.qty, 10)));
		}
	});
	document.addEventListener("change", (e) => {
		const line = e.target.closest("[data-line]");
		if (line && e.target.matches("input")) { cart.set(line.dataset.line, parseInt(e.target.value, 10) || 1); renderCart(); }
	});
	window.addEventListener("storage", (e) => { if (e.key === KEY) { lines = store(KEY) || []; priced = null; renderCount(); renderCart(); } });
	if (document.querySelector("[data-checkout]")) renderCart();
}

export function initCheckout() {
	const form = $("[data-checkout-form]");
	if (!form) return;
	const err = $("[data-form-error]");  // in the order summary, outside the form
	const buttons = $$("[data-submit]");
	const busy = (on) => buttons.forEach((b) => { b.disabled = on; b.classList.toggle("is-loading", on); });
	form.addEventListener("submit", async (e) => {
		e.preventDefault();
		err.hidden = true;
		const fd = Object.fromEntries(new FormData(form).entries());
		const mobile = (fd.mobile || "").replace(/[\s-]/g, "").replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d));
		const bad = [];
		if ((fd.name || "").trim().length < 2) bad.push("name");
		if (!/^(\+?966|0)?5\d{8}$/.test(mobile)) bad.push("mobile");
		if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((fd.email || "").trim())) bad.push("email");
		const delivery = fd.fulfilment === "Delivery";
		const num = (v) => (v || "").replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d)).replace(/\s/g, "");
		if (delivery) {
			$$("[data-delivery-req]", form).forEach((i) => { if (!i.closest("[hidden]") && (i.value || "").trim().length < 1) bad.push(i.name); });
			if ((fd.district || "").trim().length === 1) bad.push("district");
			const na = num(fd.national_address).toUpperCase();
			if (na && !/^[A-Z]{4}\d{4}$/.test(na)) bad.push("national_address");
			else if (na && /^\d{4}$/.test(num(fd.building_number)) && na.slice(4) !== num(fd.building_number)) bad.push("national_address_mismatch");
			if (fd.additional_number && !/^\d{4}$/.test(num(fd.additional_number))) bad.push("additional_number");
			if (fd.postal_code && !/^\d{5}$/.test(num(fd.postal_code))) bad.push("postal_code");
		}
		$$("input, select", form).forEach((i) => i.classList.toggle("is-invalid", bad.includes(i.name)
			|| (i.name === "national_address" && bad.includes("national_address_mismatch"))));
		if (bad.length && delivery && $("[data-addr-saved]", form) && !$("[data-addr-saved]", form).hidden) {
			$("[data-addr-saved] [data-addr-edit]", form)?.click();  // show the fields that need fixing
		}
		if (bad.length) {
			err.textContent = bad.includes("name") ? t("أدخل اسمك", "Please enter your name")
				: bad.includes("mobile") ? t("أدخل رقم جوال سعودي صحيح (05XXXXXXXX)", "Enter a valid Saudi mobile number (05XXXXXXXX)")
				: bad.includes("email") ? t("أدخل بريداً إلكترونياً صحيحاً", "Enter a valid email address")
				: bad.includes("national_address") ? t("العنوان الوطني المختصر 4 حروف و4 أرقام، مثل RJYB2357", "The short national address is 4 letters and 4 digits, e.g. RJYB2357")
				: bad.includes("national_address_mismatch") ? t("آخر 4 أرقام في العنوان المختصر هي رقم المبنى، تأكد منهما", "The short address ends with the building number. Please check both")
				: bad.includes("additional_number") ? t("الرقم الإضافي 4 أرقام", "The additional number is 4 digits")
				: bad.includes("postal_code") ? t("الرمز البريدي 5 أرقام", "The postal code is 5 digits")
				: t("أكمل عنوان التوصيل: المدينة والحي والشارع ورقم المبنى", "Complete the delivery address: city, district, street and building number");
			err.hidden = false;
			const first = form.querySelector(".is-invalid");
			if (first) { first.scrollIntoView({ behavior: "smooth", block: "center" }); first.focus({ preventScroll: true }); }
			return;
		}
		busy(true);
		try {
			const res = await api("darat.storefront.api.place_order", { order: { ...fd, mobile, lines: cart.lines(), my_laptop: (store("darat_mylaptop") || {}).slug || "" } }, "POST");
			try {  // remembered on this device so /track is pre-filled next time
				localStorage.setItem("darat_track", JSON.stringify({ id: res.order, email: (fd.email || "").trim(), mobile }));
			} catch (e2) { /* private mode */ }
			if (delivery) rememberAddress(form);  // next order: one tap
			if (res.pay_url) {
				// online: the cart stays until the payment is confirmed (back from /pay = cart still there);
				// the order page clears it once paid. Submitting again retires this unpaid draft on the server.
				store(PAYING, { id: res.order, token: res.token, t: Date.now() });
			} else {
				cart.clear();
				store(DRAFT, null);
			}
			// online payment: straight to the secure payment page; the order page offers «ادفع الآن» if that fails
			location.href = res.pay_url || `/order/${encodeURIComponent(res.token)}?new=1`;
		} catch (ex) {
			err.textContent = ex.message; err.hidden = false;
			busy(false);
		}
	});
}

// ---------------------------------------------------------------- checkout extras
export function initCheckoutExtras() {
	const form = $("[data-checkout-form]");
	if (!form) return;
	let couponOk = null;


	// pickup or delivery
	const showFulfil = () => {
		const delivery = form.querySelector("[name=fulfilment]:checked")?.value === "Delivery";
		if (delivery) initDeliveryAddress(form);
		$$(".ds-delivery-only", form).forEach((x) => (x.hidden = !delivery));
		$$(".ds-pickup-only", form).forEach((x) => (x.hidden = delivery));
		applyDelivery();
	};
	// the main button says what happens next: «متابعة للدفع» for online payment
	const syncSubmitLabel = () => {
		const delivery = form.querySelector("[name=fulfilment]:checked")?.value === "Delivery";
		const pay = form.querySelector(delivery ? "[name=payment]:checked" : "[name=pickup_payment]:checked")?.value;
		$$("[data-submit-label]").forEach((el) => {
			el.dataset.idle = el.dataset.idle || el.textContent;
			el.textContent = pay === "online" ? t("متابعة للدفع الآمن", "Continue to secure payment") : el.dataset.idle;
		});
	};
	// details typed earlier on this device survive a reload / coming back from the payment page
	const KEEP = ["name", "mobile", "email", "notes"];
	const draft = store(DRAFT) || {};
	KEEP.forEach((n) => { const i = form.elements[n]; if (i && !i.value && draft[n]) i.value = draft[n]; });
	["fulfilment", "payment", "pickup_payment"].forEach((n) => {
		const r = draft[n] && form.querySelector(`[name=${n}][value="${CSS.escape(draft[n])}"]`);
		if (r) r.checked = true;
	});
	// the cards, sections and button must follow the restored choices (else the highlighted card and the
	// checked radio disagree, and clicking the "unselected" card does nothing)
	$$("[data-fulfil], [data-pay]", form).forEach((m) => $$("input", m).forEach((i) => i.closest(".ds-method__opt")?.classList.toggle("is-on", i.checked)));
	showFulfil();
	let saveT;
	form.addEventListener("input", () => { clearTimeout(saveT); saveT = setTimeout(saveDraft, 400); });
	form.addEventListener("change", saveDraft);
	function saveDraft() {
		const d = {};
		[...KEEP, "fulfilment", "payment", "pickup_payment"].forEach((n) => {
			const el = form.elements[n];
			const v = el && (el instanceof RadioNodeList ? el.value : el.value);
			if (v) d[n] = String(v).slice(0, 500);
		});
		store(DRAFT, d);
	}
	// an online order from this cart is waiting for payment: offer to finish it
	const paying = store(PAYING);
	const note = $("[data-paying]");
	if (note && paying && lines.length && Date.now() - paying.t < 24 * 3600e3) {
		note.querySelector("[data-paying-id]").textContent = paying.id;
		note.querySelector("[data-paying-link]").href = `/order/${encodeURIComponent(paying.token)}`;
		note.hidden = false;
	}

	syncSubmitLabel();
	form.addEventListener("change", (e) => {
		const radio = e.target.closest("[data-fulfil] input, [data-pay] input");
		if (!radio) return;
		$$("input", radio.closest(".ds-method")).forEach((i) => i.closest(".ds-method__opt").classList.toggle("is-on", i.checked));
		showFulfil();
		syncSubmitLabel();
	});

	// coupon
	const msg = $("[data-coupon-msg]");
	const applyCoupon = async () => {
		const code = $("[data-coupon-input]").value.trim();
		msg.hidden = false; msg.className = "ds-coupon__msg";
		if (!code) { couponOk = null; msg.hidden = true; $("[data-disc-row]").hidden = true; return; }
		try {
			const r = await api("darat.storefront.api.check_coupon", { code, lines: cart.lines() }, "POST");
			if (r.error) { couponOk = null; msg.textContent = r.error; msg.classList.add("is-err"); $("[data-disc-row]").hidden = true; return; }
			couponOk = code;
			msg.textContent = t(`تم تطبيق الخصم: ${money(r.discount_gross)}`, `Discount applied: ${money(r.discount_gross)}`); msg.classList.add("is-ok");
			$("[data-disc-row]").hidden = false; $("[data-disc-val]").textContent = "− " + money(r.discount_gross);
			goodsTotal = r.total; applyDelivery();
		} catch (e) { msg.textContent = e.message; msg.classList.add("is-err"); }
	};
	$("[data-coupon-apply]")?.addEventListener("click", applyCoupon);
	$("[data-coupon-input]")?.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(); } });

	form.dataset.coupon = "";
	form.addEventListener("submit", () => { const c = $("[data-coupon-input]"); if (c && !couponOk) c.value = ""; }, true);

	// customers also added
	const sug = $("[data-suggest]");
	const loadSug = async () => {
		if (!sug || !cart.lines().length) { if (sug) sug.hidden = true; return; }
		try {
			const items = await api("darat.storefront.api.cart_suggestions", { codes: cart.lines().map((l) => l.c) }, "POST");
			$("[data-suggest-list]").innerHTML = items.map((i) => `<div class="ds-sugitem">
				<a href="/p/${esc(i.u)}" class="ds-sugitem__img" aria-label="${esc(i.n)}">${i.i ? `<img src="${esc(i.i)}" alt="">` : `<svg viewBox="0 0 160 120"><use href="#part-${esc(i.ic)}"/></svg>`}</a>
				<div><a href="/p/${esc(i.u)}">${esc(document.documentElement.lang.startsWith("ar") && i.a ? i.a : i.n)}</a>${showPrices ? `<b>${money(i.p)}</b>` : ""}</div>
				<button type="button" class="ds-btn ds-btn--icon ds-btn--primary" data-add="${esc(i.c)}" aria-label="+">${icon("plus")}</button></div>`).join("");
			sug.hidden = !items.length;
		} catch (e) { sug.hidden = true; }
	};
	loadSug();
	document.addEventListener("darat:cart", () => setTimeout(loadSug, 300));
}
